// skipcq: JS-W1028, JS-0128
import React, { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import PauseCircleOutline from '@mui/icons-material/PauseCircleOutline';
import { CRAWL_TYPES } from './scheduleFormConstants';

const SAMPLE_FRACTION = 0.1;

function buildCrawlTypePayload(crawlTypeFilter) {
  return crawlTypeFilter ? { crawlType: crawlTypeFilter } : {};
}

/** @param {{ open: boolean, crawlTypeFilter: string, busy: boolean, onCancel: () => void, onConfirm: () => void }} props */
function PauseAllConfirmDialog({ open, crawlTypeFilter, busy, onCancel, onConfirm }) {
  const filterLabel = crawlTypeFilter || 'all crawl types';
  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel}>
      <DialogTitle>Pause all schedules?</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          Stops dispatch for every non-paused schedule matching <strong>{filterLabel}</strong>.
          Schedule documents remain in Firestore for later resume or replan.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained" disabled={busy}>
          {busy ? 'Pausing…' : 'Pause all'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** @param {{ open: boolean, crawlTypeFilter: string, busy: boolean, confirmText: string, onConfirmTextChange: (event: object) => void, onCancel: () => void, onConfirm: () => void }} props */
function CancelAllConfirmDialog({
  open,
  crawlTypeFilter,
  busy,
  confirmText,
  onConfirmTextChange,
  onCancel,
  onConfirm,
}) {
  const filterLabel = crawlTypeFilter || 'all crawl types';
  const canConfirm = confirmText.trim().toUpperCase() === 'CANCEL';
  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel}>
      <DialogTitle>Cancel all schedules?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Permanently deletes every <code>crawlSchedules</code> document matching{' '}
          <strong>{filterLabel}</strong>. Execution history in <code>crawlExecutions</code> is kept.
          This cannot be undone.
        </Typography>
        <TextField
          fullWidth
          label='Type "CANCEL" to confirm'
          value={confirmText}
          onChange={onConfirmTextChange}
          disabled={busy}
          size="small"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          Close
        </Button>
        <Button
          onClick={onConfirm}
          color="error"
          variant="contained"
          disabled={busy || !canConfirm}
        >
          {busy ? 'Deleting…' : 'Delete all schedules'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** @param {{ open: boolean, crawlTypeFilter: string, dryRun: boolean, acknowledged: boolean, busy: boolean, onAcknowledgedChange: (event: object) => void, onCancel: () => void, onConfirm: () => void }} props */
function SampleReplanConfirmDialog({
  open,
  crawlTypeFilter,
  dryRun,
  acknowledged,
  busy,
  onAcknowledgedChange,
  onCancel,
  onConfirm,
}) {
  const filterLabel = crawlTypeFilter || 'all crawl types';
  const dryRunNote = dryRun ? ' (dry run — no writes)' : '';
  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel}>
      <DialogTitle>Replan with 10% sample?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Runs the planner for a deterministic ~10% of eligible breweries for{' '}
          <strong>{filterLabel}</strong>
          {dryRunNote}. Recommended workflow: cancel all schedules first, then run this sample
          replan.
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={acknowledged}
              onChange={onAcknowledgedChange}
              disabled={busy}
              color="primary"
            />
          }
          label="I have cancelled existing schedules (or intend a clean slate)"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained" disabled={busy || !acknowledged}>
          {busy ? 'Planning…' : 'Replan 10% sample'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CostControlsCrawlTypeSelect({ crawlTypeFilter, onCrawlTypeFilterChange, controlsDisabled }) {
  return (
    <FormControl size="small" sx={{ minWidth: 220 }}>
      <InputLabel id="cost-controls-crawl-type-label">Crawl type filter</InputLabel>
      <Select
        labelId="cost-controls-crawl-type-label"
        label="Crawl type filter"
        value={crawlTypeFilter}
        onChange={onCrawlTypeFilterChange}
        disabled={controlsDisabled}
      >
        <MenuItem value="">All crawl types</MenuItem>
        {CRAWL_TYPES.map((crawlTypeOption) => (
          <MenuItem key={crawlTypeOption} value={crawlTypeOption}>
            {crawlTypeOption}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function CostControlsActionButtons({
  controlsDisabled,
  busy,
  onPauseOpen,
  onCancelOpen,
  onSampleOpen,
}) {
  return (
    <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
      {busy ? <CircularProgress size={24} aria-label="Cost control operation in progress" /> : null}
      <Button
        variant="outlined"
        startIcon={<PauseCircleOutline />}
        onClick={onPauseOpen}
        disabled={controlsDisabled}
      >
        Pause all schedules
      </Button>
      <Button
        variant="outlined"
        color="error"
        startIcon={<DeleteOutline />}
        onClick={onCancelOpen}
        disabled={controlsDisabled}
      >
        Cancel all schedules
      </Button>
      <Button variant="contained" onClick={onSampleOpen} disabled={controlsDisabled}>
        Replan 10% sample
      </Button>
    </Box>
  );
}

/**
 * @param {{
 *   planCrawlSchedules: (payload: object) => Promise<{ data: object }>,
 *   crawlTypeFilter: string,
 *   onCrawlTypeFilterChange: (event: object) => void,
 *   dryRun: boolean,
 *   busy: boolean,
 *   setBusy: (nextBusy: boolean) => void,
 *   onOperationComplete: () => Promise<void> | void,
 *   onSuccess: (message: string) => void,
 *   onError: (message: string) => void,
 * }} props
 */
function CostControlsPanel({
  planCrawlSchedules,
  crawlTypeFilter,
  onCrawlTypeFilterChange,
  dryRun,
  busy,
  setBusy,
  onOperationComplete,
  onSuccess,
  onError,
}) {
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [sampleDialogOpen, setSampleDialogOpen] = useState(false);
  const [cancelConfirmText, setCancelConfirmText] = useState('');
  const [sampleAcknowledged, setSampleAcknowledged] = useState(false);

  const controlsDisabled = busy;

  const handlePauseOpen = useCallback(() => {
    setPauseDialogOpen(true);
  }, []);

  const handlePauseClose = useCallback(() => {
    if (!busy) {
      setPauseDialogOpen(false);
    }
  }, [busy]);

  const handleCancelOpen = useCallback(() => {
    setCancelConfirmText('');
    setCancelDialogOpen(true);
  }, []);

  const handleCancelClose = useCallback(() => {
    if (!busy) {
      setCancelDialogOpen(false);
      setCancelConfirmText('');
    }
  }, [busy]);

  const handleSampleOpen = useCallback(() => {
    setSampleAcknowledged(false);
    setSampleDialogOpen(true);
  }, []);

  const handleSampleClose = useCallback(() => {
    if (!busy) {
      setSampleDialogOpen(false);
      setSampleAcknowledged(false);
    }
  }, [busy]);

  const handleCancelConfirmTextChange = useCallback((event) => {
    setCancelConfirmText(event.target.value);
  }, []);

  const handleSampleAcknowledgedChange = useCallback((event) => {
    setSampleAcknowledged(event.target.checked === true);
  }, []);

  const runCallable = useCallback(
    async function runCostControlCallable(payload, successMessageBuilder) {
      setBusy(true);
      let successMessage = '';
      try {
        const result = await planCrawlSchedules(payload);
        const data = result.data ?? {};
        successMessage = successMessageBuilder(data);
        try {
          await onOperationComplete();
          onSuccess(successMessage);
        } catch (refreshError) {
          console.error(refreshError);
          onSuccess(
            `${successMessage} Planner run list refresh failed; reload the page to see updates.`,
          );
        }
      } catch (err) {
        console.error(err);
        const firebaseMessage =
          err && typeof err === 'object' && 'message' in err ? String(err.message) : String(err);
        onError(firebaseMessage);
      } finally {
        setBusy(false);
      }
    },
    [onError, onOperationComplete, onSuccess, planCrawlSchedules, setBusy],
  );

  const handlePauseConfirm = useCallback(async () => {
    await runCallable(
      { operation: 'pause_all', ...buildCrawlTypePayload(crawlTypeFilter) },
      (data) => {
        const pausedCount = typeof data.pausedCount === 'number' ? data.pausedCount : 0;
        return `Paused ${pausedCount} schedule(s).`;
      },
    );
    setPauseDialogOpen(false);
  }, [crawlTypeFilter, runCallable]);

  const handleCancelConfirm = useCallback(async () => {
    await runCallable(
      { operation: 'cancel_all', ...buildCrawlTypePayload(crawlTypeFilter) },
      (data) => {
        const deletedCount = typeof data.deletedCount === 'number' ? data.deletedCount : 0;
        return `Deleted ${deletedCount} schedule document(s).`;
      },
    );
    setCancelDialogOpen(false);
    setCancelConfirmText('');
  }, [crawlTypeFilter, runCallable]);

  const handleSampleConfirm = useCallback(async () => {
    await runCallable(
      {
        operation: 'plan',
        sampleFraction: SAMPLE_FRACTION,
        dryRun,
        ...buildCrawlTypePayload(crawlTypeFilter),
      },
      (data) => {
        const upserted = typeof data.schedulesUpserted === 'number' ? data.schedulesUpserted : 0;
        const skippedSample =
          typeof data.schedulesSkippedSample === 'number' ? data.schedulesSkippedSample : 0;
        const breweries =
          typeof data.breweriesDiscovered === 'number' ? data.breweriesDiscovered : 0;
        const dryRunRan = data.dryRun === true;
        if (dryRunRan) {
          return `Sample dry run complete (${breweries} breweries surveyed; ${upserted} upserts would apply; ${skippedSample} skipped by sample gate).`;
        }
        return `Sample replan finished (${breweries} breweries; ${upserted} schedules upserted; ${skippedSample} skipped by sample gate).`;
      },
    );
    setSampleDialogOpen(false);
    setSampleAcknowledged(false);
  }, [crawlTypeFilter, dryRun, runCallable]);

  return (
    <Box sx={{ mb: 3 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="subtitle2" component="div" gutterBottom>
          Cost controls (dev/staging)
        </Typography>
        <Typography variant="body2">
          Reduce duplicate crawl cost: optionally pause all, cancel all schedule documents, then
          replan a deterministic 10% brewery sample. Restore full coverage with cancel all followed
          by a full replan (no sample fraction).
        </Typography>
      </Alert>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <CostControlsCrawlTypeSelect
            crawlTypeFilter={crawlTypeFilter}
            onCrawlTypeFilterChange={onCrawlTypeFilterChange}
            controlsDisabled={controlsDisabled}
          />
          <CostControlsActionButtons
            controlsDisabled={controlsDisabled}
            busy={busy}
            onPauseOpen={handlePauseOpen}
            onCancelOpen={handleCancelOpen}
            onSampleOpen={handleSampleOpen}
          />
        </Box>
      </Paper>
      <PauseAllConfirmDialog
        open={pauseDialogOpen}
        crawlTypeFilter={crawlTypeFilter}
        busy={busy}
        onCancel={handlePauseClose}
        onConfirm={handlePauseConfirm}
      />
      <CancelAllConfirmDialog
        open={cancelDialogOpen}
        crawlTypeFilter={crawlTypeFilter}
        busy={busy}
        confirmText={cancelConfirmText}
        onConfirmTextChange={handleCancelConfirmTextChange}
        onCancel={handleCancelClose}
        onConfirm={handleCancelConfirm}
      />
      <SampleReplanConfirmDialog
        open={sampleDialogOpen}
        crawlTypeFilter={crawlTypeFilter}
        dryRun={dryRun}
        acknowledged={sampleAcknowledged}
        busy={busy}
        onAcknowledgedChange={handleSampleAcknowledgedChange}
        onCancel={handleSampleClose}
        onConfirm={handleSampleConfirm}
      />
    </Box>
  );
}

export default CostControlsPanel;
